import type { HTMLAttributes, ReactNode } from "react";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding jak w design systemie: md 30px, sm 22px, lg 34px, flush 0. */
  pad?: "md" | "sm" | "lg" | "flush";
  hover?: boolean;
  accent?: boolean;
}

/** Karta z design systemu: promień 18px, hairline, hover unoszący o 3px. */
export function Card({ pad = "md", hover, accent, className, children, ...rest }: CardProps) {
  const cls = [
    "ui-card",
    pad === "md" ? "" : `ui-card--${pad}`,
    hover ? "ui-card--hover" : "",
    accent ? "ui-card--accent" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
}

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  accent?: boolean;
}

/**
 * Panel spoza design systemu — promień 16px bez własnego paddingu.
 * Prototyp panelu operatora i strony klienta buduje z niego swoje sekcje,
 * każdorazowo podając padding właściwy dla danego miejsca.
 */
export function Panel({ accent, className, children, ...rest }: PanelProps) {
  return (
    <div className={["ui-panel", accent ? "ui-panel--accent" : "", className ?? ""].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export interface TileProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Kolor bazowy ikony — tło dostaje 12% krycia. */
  color?: string;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
}

/** Kafel z design systemu — używany w kroku „Branże" kreatora rejestracji. */
export function Tile({ title, description, icon, color, selected, onSelect, disabled }: TileProps) {
  const tint = color ? hexToRgba(color, 0.12) : undefined;
  return (
    <button
      type="button"
      className="ui-tile"
      aria-pressed={selected ?? false}
      onClick={onSelect}
      disabled={disabled}
    >
      {icon ? (
        <span className="ui-tile__icon" style={tint ? { background: tint, color } : undefined}>
          {icon}
        </span>
      ) : null}
      <span className="ui-tile__title">{title}</span>
      {description ? <span className="ui-tile__desc">{description}</span> : null}
    </button>
  );
}

export function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = Number.parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
